/**
 * Student dashboard — profile, odd/even term schedule, subject feedback board
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import RoleDashboardShell from '../components/RoleDashboardShell';
import {
  studentMenuItems,
  studentMenuGroups,
  isValidStudentMenuId,
} from '../config/roleDashboardMenus';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';

const STATUS_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'pending', label: 'Pending' },
  { id: 'completed', label: 'Completed' },
  { id: 'upcoming', label: 'Upcoming' },
];

const STATUS_HINT = {
  active: 'Window is open — submit now',
  pending: 'Waiting on faculty or assignment',
  completed: 'Submitted and locked',
  upcoming: 'Opens with the next cycle',
};

function openStudentWindow(path) {
  const base = process.env.PUBLIC_URL || '';
  const url = `${window.location.origin}${base}${path}`;
  const popup = window.open(url, '_blank');
  if (popup) {
    popup.opener = null;
    return;
  }
  window.location.assign(url);
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function initials(name) {
  return (name || 'ST')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

function Field({ label, value }) {
  return (
    <div className="profile-field">
      <dt>{label}</dt>
      <dd>{value || '—'}</dd>
    </div>
  );
}

export default function StudentDashboard() {
  const { user } = useAuth();
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeAdmissionCycle, setActiveAdmissionCycle] = useState(null);
  const [admissionHasSubmitted, setAdmissionHasSubmitted] = useState(false);
  const [admissionQuestions, setAdmissionQuestions] = useState([]);
  const [admissionAnswers, setAdmissionAnswers] = useState({});
  const [admissionRatings, setAdmissionRatings] = useState({ experienceRating: 4, processRating: 4, supportRating: 4 });
  const [admissionSubmitting, setAdmissionSubmitting] = useState(false);
  const [admissionError, setAdmissionError] = useState('');

  const fetchWorkspace = useCallback(async () => {
    try {
      const [boardRes, admissionRes] = await Promise.all([
        api.get('/feedback/dashboard'),
        api.get('/feedback/active-admission-cycle').catch(() => ({ data: { data: { cycle: null, hasSubmitted: false, questions: [] } } })),
      ]);
      setWorkspace(boardRes.data?.data || null);
      const admissionData = admissionRes.data?.data || {};
      setActiveAdmissionCycle(admissionData.cycle || null);
      setAdmissionHasSubmitted(Boolean(admissionData.hasSubmitted));
      setAdmissionQuestions(Array.isArray(admissionData.questions) ? admissionData.questions : []);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to load student dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWorkspace();
    const onFocus = () => fetchWorkspace();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchWorkspace]);

  const profile = workspace?.profile;
  const term = workspace?.term;
  const cycle = workspace?.cycle;
  const schedule = workspace?.schedule || [];
  const stats = workspace?.stats || { active: 0, pending: 0, completed: 0, upcoming: 0 };
  const displayName = profile?.firstName
    ? `${profile.firstName} ${profile.lastName || ''}`.trim()
    : user?.email;
  const termLabel = term?.term_type === 'EVEN' ? 'Even' : term?.term_type === 'ODD' ? 'Odd' : null;

  const filteredSubjects = useMemo(() => {
    const list = workspace?.subjects || [];
    return statusFilter === 'all' ? list : list.filter((subject) => subject.status === statusFilter);
  }, [workspace, statusFilter]);

  const handleGiveFeedback = (subject) => {
    if (!subject.canSubmit) return;
    const params = new URLSearchParams({
      subjectId: subject.id,
      facultyId: subject.facultyId,
      cycleId: subject.cycleId,
      subjectName: subject.name || subject.code || '',
      facultyName: subject.facultyName || '',
    });
    openStudentWindow(`/dashboard/student/feedback?${params.toString()}`);
  };

  const handlePreview = (subject) => {
    if (!subject.submissionId) return;
    openStudentWindow(`/dashboard/student/preview/${subject.submissionId}`);
  };

  const handleSubmitAdmissionFeedback = async (e) => {
    e.preventDefault();
    setAdmissionError('');
    setAdmissionSubmitting(true);
    try {
      const answers = admissionQuestions
        .map((q) => ({ questionId: q.id, answerKey: admissionAnswers[q.id] }))
        .filter((a) => a.answerKey != null && String(a.answerKey).trim() !== '');
      await api.post('/feedback/admission/submit', {
        experienceRating: admissionRatings.experienceRating,
        processRating: admissionRatings.processRating,
        supportRating: admissionRatings.supportRating,
        answers: answers.length > 0 ? answers : undefined,
      });
      await fetchWorkspace();
    } catch (err) {
      setAdmissionError(err.response?.data?.error?.message || 'Failed to submit admission feedback');
    } finally {
      setAdmissionSubmitting(false);
    }
  };

  return (
    <RoleDashboardShell
      title="Student Dashboard"
      subtitle={`${displayName}${profile?.departmentName || profile?.department ? ` · ${profile.departmentName || profile.department}` : ''}`}
      brandTitle="Student Console"
      brandSubtitle={term ? `${term.term_type} term · ${term.academic_year}` : 'No active term'}
      menuItems={studentMenuItems}
      menuGroups={studentMenuGroups}
      isValidMenuId={isValidStudentMenuId}
      loading={loading}
      error={error}
    >
      {({ activeMenu }) => {
        if (activeMenu === 'overview') {
          return (
            <div className="space-y-6">
              <div className="stu-hero">
                <div className="stu-hero-row">
                  <div className="stu-hero-main">
                    <span className="stu-avatar">{initials(displayName)}</span>
                    <div>
                      <p className="text-sm text-white/80 mb-1">Welcome back</p>
                      <h2 className="text-2xl font-semibold m-0">{displayName}</h2>
                      <p className="mt-1 text-white/85 text-sm">
                        {[profile?.prn, profile?.departmentName || profile?.department, profile?.semesterTitle, profile?.academicYear || term?.academic_year]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                    </div>
                  </div>
                  <div className="stu-hero-cycle">
                    <p className="text-xs uppercase tracking-wide text-white/70 m-0">Active semester window</p>
                    <p className="font-semibold m-0 mt-1">
                      {termLabel ? `${termLabel} semester` : 'Term not published'}
                      {term?.academic_year ? ` · ${term.academic_year}` : ''}
                    </p>
                    <p className="text-sm text-white/80 m-0 mt-1">
                      {cycle?.name || 'Faculty cycle not opened yet'}
                    </p>
                    {cycle ? (
                      <p className="text-xs text-white/70 m-0 mt-1">
                        {formatDate(cycle.start_date)} – {formatDate(cycle.end_date)}
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="stu-stat-grid">
                {[
                  { key: 'active', label: 'Active' },
                  { key: 'pending', label: 'Pending' },
                  { key: 'completed', label: 'Completed' },
                  { key: 'upcoming', label: 'Upcoming' },
                ].map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={`stu-stat stu-stat-${item.key} text-left`}
                    onClick={() => setStatusFilter(item.key)}
                  >
                    <p className="text-xs uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>{item.label}</p>
                    <p className="stu-stat-value">{stats[item.key] || 0}</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{STATUS_HINT[item.key]}</p>
                  </button>
                ))}
              </div>

              <div className="stu-filter-bar">
                {STATUS_FILTERS.map((filter) => (
                  <button
                    key={filter.id}
                    type="button"
                    onClick={() => setStatusFilter(filter.id)}
                    className={`stu-filter ${statusFilter === filter.id ? 'is-on' : ''}`}
                  >
                    {filter.label}
                    {filter.id !== 'all' ? ` · ${stats[filter.id] || 0}` : ` · ${workspace?.subjects?.length || 0}`}
                  </button>
                ))}
              </div>

              {filteredSubjects.length === 0 ? (
                <div className="admin-empty">
                  <p className="admin-empty-title">No subjects to show</p>
                  <p className="admin-empty-text">When you are enrolled for this term, every subject appears here with live feedback status.</p>
                </div>
              ) : (
                <div className="stu-subject-grid">
                  {filteredSubjects.map((subject) => (
                    <article key={subject.id} className={`stu-subject-card is-${subject.status}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>{subject.code}</p>
                          <h3 className="text-lg font-semibold m-0" style={{ color: 'var(--text-primary)' }}>{subject.name}</h3>
                        </div>
                        <span className={`stu-chip stu-chip-${subject.status}`}>{subject.status}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="stu-avatar-sm">{initials(subject.facultyName || 'NA')}</span>
                        <div className="min-w-0">
                          <p className="text-sm font-medium m-0" style={{ color: 'var(--text-primary)' }}>
                            {subject.facultyName || 'Faculty not assigned yet'}
                          </p>
                          <p className="text-xs m-0" style={{ color: 'var(--text-muted)' }}>
                            {subject.isElective ? 'Elective' : 'Core'}
                            {subject.credits != null ? ` · ${subject.credits} credits` : ''}
                            {subject.designation ? ` · ${subject.designation}` : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mt-auto">
                        {(subject.status === 'active' || subject.status === 'pending') && (
                          <button
                            type="button"
                            className="btn-primary"
                            disabled={!subject.canSubmit}
                            onClick={() => handleGiveFeedback(subject)}
                            title={subject.canSubmit ? 'Opens the feedback form in a new tab' : 'Waiting for faculty assignment'}
                          >
                            Give feedback
                          </button>
                        )}
                        {subject.status === 'completed' && (
                          <button type="button" className="btn-secondary" onClick={() => handlePreview(subject)}>
                            Preview
                          </button>
                        )}
                        {subject.status === 'upcoming' && (
                          <button type="button" className="btn-secondary" disabled>
                            Give feedback
                          </button>
                        )}
                        <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
                          {subject.status === 'completed'
                            ? 'Locked · cannot edit'
                            : subject.canSubmit
                              ? 'Opens in a new tab'
                              : STATUS_HINT[subject.status]}
                        </span>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </div>
          );
        }

        if (activeMenu === 'profile') {
          return (
            <div className="admin-card">
              <div className="admin-card-body">
                <div className="stu-identity">
                  <span className="stu-avatar-sm" style={{ width: '3.25rem', height: '3.25rem', fontSize: '1.1rem' }}>
                    {initials(displayName)}
                  </span>
                  <div>
                    <h2 className="admin-section-title mb-1">Student profile</h2>
                    <p className="admin-section-subtitle mb-0">Personal identity used across this institute. Details are read-only.</p>
                  </div>
                </div>
                {!profile ? (
                  <div className="admin-empty">
                    <p className="admin-empty-title">Profile not linked yet</p>
                    <p className="admin-empty-text">Ask admin to attach a student master record to this login.</p>
                  </div>
                ) : (
                  <dl className="profile-grid">
                    <Field label="Full name" value={displayName} />
                    <Field label="PRN / Roll no." value={profile.prn || profile.rollNumber} />
                    <Field label="Email" value={profile.email || user?.email} />
                    <Field label="Gender" value={profile.gender} />
                    <Field label="Date of birth" value={profile.dateOfBirth ? new Date(profile.dateOfBirth).toLocaleDateString() : ''} />
                    <Field label="Contact" value={profile.contactNumber} />
                    <Field label="Guardian" value={profile.guardianName} />
                    <Field label="Guardian contact" value={profile.guardianContact} />
                    <Field label="Address" value={profile.address} />
                    <Field label="Status" value={profile.studentStatus} />
                    <Field label="Admission year" value={profile.admissionYear} />
                    <Field label="Admission category" value={profile.admissionCategory} />
                  </dl>
                )}
              </div>
            </div>
          );
        }

        if (activeMenu === 'academics') {
          return (
            <div className="space-y-6">
              <div className="stu-hero">
                <p className="text-sm text-white/80 m-0">Department · Program · Semester</p>
                <h2 className="text-2xl font-semibold m-0 mt-1">
                  {profile?.departmentName || profile?.department || 'Department'}
                </h2>
                <p className="mt-2 text-white/85 text-sm m-0">
                  {[profile?.programTitle || profile?.programCode, profile?.semesterTitle, termLabel ? `${termLabel} term` : null, profile?.academicYear || term?.academic_year]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
              <div className="admin-card">
                <div className="admin-card-body">
                  <h2 className="admin-section-title">Academic identity</h2>
                  <dl className="profile-grid">
                    <Field label="Institute" value={user?.institute?.name} />
                    <Field label="Department" value={profile?.departmentName || profile?.department} />
                    <Field label="Department code" value={profile?.departmentCode} />
                    <Field label="Program" value={profile?.programTitle || profile?.programCode} />
                    <Field label="Year of study" value={profile?.yearOfStudy} />
                    <Field label="Current semester" value={profile?.semesterTitle || (profile?.semesterNumber ? `Semester ${profile.semesterNumber}` : '')} />
                    <Field label="Division / section" value={profile?.currentDivisionCode || profile?.section} />
                    <Field label="Batch" value={profile?.batch} />
                    <Field label="Academic year" value={profile?.academicYear || term?.academic_year} />
                  </dl>
                </div>
              </div>
              <div className="admin-card">
                <div className="admin-card-body">
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <h2 className="admin-section-title mb-0">Active semester window</h2>
                    {term ? (
                      <span className={`stu-chip ${term.term_type === 'EVEN' ? 'stu-chip-even' : 'stu-chip-odd'}`}>
                        {term.term_type} semester
                      </span>
                    ) : null}
                  </div>
                  {term ? (
                    <dl className="profile-grid">
                      <Field label="Academic year" value={term.academic_year} />
                      <Field label="Term" value={term.term_type === 'EVEN' ? 'Even (2, 4, 6, 8)' : 'Odd (1, 3, 5, 7)'} />
                      <Field label="Semesters in this window" value={(term.semester_numbers || []).join(', ')} />
                      <Field label="Starts" value={formatDate(term.start_at)} />
                      <Field label="Ends" value={formatDate(term.end_at)} />
                      <Field label="Feedback cycle" value={cycle?.name || 'Not opened yet'} />
                    </dl>
                  ) : (
                    <p style={{ color: 'var(--text-muted)' }}>Admin has not published an odd/even academic term calendar yet.</p>
                  )}
                </div>
              </div>
              <div className="admin-card">
                <div className="admin-card-body">
                  <h2 className="admin-section-title">Step-by-step feedback schedule</h2>
                  <p className="admin-section-subtitle">Student lifecycle stages for this institute. Live faculty/subject stages stay highlighted during the active cycle.</p>
                  {schedule.length === 0 ? (
                    <p style={{ color: 'var(--text-muted)' }}>No student lifecycle stages configured yet.</p>
                  ) : (
                    <div className="stu-timeline">
                      {schedule.map((stage, index) => (
                        <div key={stage.id} className={`stu-timeline-item ${stage.isCurrent ? 'current' : ''}`}>
                          <span className={`stu-chip ${stage.isCurrent ? 'stu-chip-active' : 'stu-chip-upcoming'}`}>
                            {String(index + 1).padStart(2, '0')}
                          </span>
                          <div>
                            <p className="font-semibold m-0" style={{ color: 'var(--text-primary)' }}>{stage.name}</p>
                            <p className="text-sm m-0" style={{ color: 'var(--text-secondary)' }}>
                              {stage.applies_to?.replace(/_/g, ' ')}
                              {stage.isCurrent ? ' · Live now' : ''}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        }

        if (activeMenu === 'admission') {
          return (
            <div className="admin-card">
              <div className="admin-card-body">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600">
                    <ChartBarIcon className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="admin-section-title">Admission feedback</h2>
                    <p className="admin-section-subtitle mb-0">
                      {activeAdmissionCycle
                        ? (admissionHasSubmitted ? 'Already submitted for this cycle.' : `Cycle: ${activeAdmissionCycle.name || activeAdmissionCycle.academic_year}`)
                        : 'No active admission cycle.'}
                    </p>
                  </div>
                </div>
                {!activeAdmissionCycle ? (
                  <div className="admin-empty">
                    <p className="admin-empty-title">No admission cycle</p>
                    <p className="admin-empty-text">The institute admin may start one later.</p>
                  </div>
                ) : admissionHasSubmitted ? (
                  <div className="flex items-center gap-3 text-emerald-700">
                    <CheckCircleSolidIcon className="h-10 w-10" />
                    <p>Thank you. Admission feedback is locked after submit.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmitAdmissionFeedback} className="space-y-5">
                    {admissionError ? <div className="alert-error">{admissionError}</div> : null}
                    {admissionQuestions.sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)).map((q) => (
                      <div key={q.id}>
                        <p className="font-medium mb-2" style={{ color: 'var(--text-primary)' }}>{q.questionText || q.question_text}</p>
                        <div className="flex flex-wrap gap-3">
                          {(q.options || []).map((opt) => (
                            <label key={opt} className="flex items-center gap-2 cursor-pointer">
                              <input type="radio" name={`admission-q-${q.id}`} checked={(admissionAnswers[q.id] || '') === opt} onChange={() => setAdmissionAnswers((prev) => ({ ...prev, [q.id]: opt }))} />
                              <span>{opt}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                    <div className="grid gap-3 sm:grid-cols-3">
                      {['experienceRating', 'processRating', 'supportRating'].map((key) => (
                        <div key={key}>
                          <label className="input-label">{key.replace('Rating', '')} (1–5)</label>
                          <select value={admissionRatings[key]} onChange={(e) => setAdmissionRatings((r) => ({ ...r, [key]: Number(e.target.value) }))} className="input">
                            {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
                          </select>
                        </div>
                      ))}
                    </div>
                    <button type="submit" disabled={admissionSubmitting} className="btn-primary">{admissionSubmitting ? 'Submitting…' : 'Submit admission feedback'}</button>
                  </form>
                )}
              </div>
            </div>
          );
        }

        return null;
      }}
    </RoleDashboardShell>
  );
}
